/**
 * liveStatusChips — la table badge ↔ libellé ↔ visage ↔ glyphe de `<LiveStatusBadges>`.
 *
 * Pure (aucun Unistyles), pour que `theme/contrast.test.ts` mesure les badges tels qu'ils
 * s'affichent, dans les deux thèmes.
 *
 * **Les poids suivent la règle d'affichage** (`sessionBadges`, Wajih, 01/10) : l'ÉTAT se lit —
 * « Terminé » porte l'aplat de l'univers, « Paiement en attente » l'aplat foncé de l'alerte,
 * « Absence signalée » le rouge ; le CONTEXTE se fait discret — jamais un aplat plein, une tinte.
 * Jusque-là c'était l'inverse : « Remplaçant » et « Replanifié » en aplat marine, « Terminé » en
 * lavande, le plus pâle de tous.
 */
import type { IconName } from "../../atoms/icons/Icon"
import type { BadgeVariant } from "../../atoms/Badge/badgeFace"
import type { UILiveSessionContextBadge, UILiveSessionStateBadge } from "../../../ports/primary/ui/types"

export type LiveStatusChip = {
    label: string
    variant: BadgeVariant
    /** La tinte de la variante plutôt que son aplat — le défaut de `<Badge>` sinon. */
    tinted?: boolean
    icon: IconName
}

export const LIVE_STATUS_CHIPS: Record<UILiveSessionStateBadge | UILiveSessionContextBadge, LiveStatusChip> = {
    // ── L'état : un seul par séance ──
    // L'aplat de l'univers (marine, blanc sur la nuit) : un état calme, pas une alerte — et le
    // seul badge d'une ligne estompée, qui doit s'y lire.
    finished: { label: "Terminé", variant: "primary", icon: "check-circle" },
    // Le seul statut qui SUSPEND la séance — il porte l'aplat foncé, écrit en blanc, pour se lire
    // de loin et tenir l'AA : l'orange plein ne porte le blanc qu'à 2,6.
    payment_blocked: { label: "Paiement en attente", variant: "warning-deep", icon: "lock" },
    // « Absence signalée » plutôt qu'« Absent » : sur une séance à venir, l'absence est une annonce
    // (de la famille ou de l'équipe), pas un constat — et c'est elle qu'« Annuler l'absence » défait.
    absent: { label: "Absence signalée", variant: "danger", icon: "times-circle" },
    // À la place d'« Aujourd'hui », dans sa tinte : le même repère, qui change de mot. `signal` est
    // le glyphe du live dans le kit — celui de « Rejoindre ».
    in_progress: { label: "En cours", variant: "success", tinted: true, icon: "signal" },
    today: { label: "Aujourd'hui", variant: "success", tinted: true, icon: "check-circle" },

    // ── Le contexte : un seul aussi, et discret ──
    // Sur la tinte de l'alerte, comme toute chip `warning` (`badgeFace`, Wajih, 30/09) : un retard
    // signalé est le seul contexte qui garde une couleur.
    late: { label: "Retard", variant: "warning", icon: "clock" },
    // Les trois autres disent ce qui distingue la séance d'une séance ordinaire, sans rien
    // demander : la chip neutre — la lavande, à l'encre du texte — et leur glyphe.
    substitute: { label: "Remplaçant", variant: "neutral", icon: "user-friends" },
    makeup_recovery: { label: "Rattrapage", variant: "neutral", icon: "redo" },
    makeup_temporary: { label: "Rattrapage", variant: "neutral", icon: "redo" },
    rescheduled: { label: "Replanifié", variant: "neutral", icon: "calendar-alt" },
}
